import { useMemo } from 'react';
import { Alert, Badge, Button, Group, Paper, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconExternalLink, IconRefresh } from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type Artifact, type Conversation } from '~api';
import { api } from '~portal/api';
import { AnchorLink, ButtonLink } from '~portal/components/common';
import { ArtifactFormatIcons } from '~portal/components/inspector/ArtifactFormatIcons';
import { ArtifactsPanel } from '~portal/components/inspector/ArtifactsPanel';
import { CopyIdButton } from '~portal/components/inspector/CopyIdButton';
import { type Stat, StatStrip } from '~portal/components/inspector/StatStrip';
import { TurnStepper } from '~portal/components/inspector/TurnStepper';
import { UserOrganisation } from '~portal/components/inspector/UserOrganisation';
import { getAppEnv } from '~portal/env';
import { logger } from '~portal/lib';
import {
  clampTurn,
  type ConversationGlance,
  conversationGlance,
  countFormats,
  formatCompact,
  formatCount,
  formatDateTime,
  formatDurationMs,
  formatRelative,
  formatUtcDateTime,
  NO_VALUE,
  parseTurnParam,
  sessionDisplay,
  turnNumberOf,
  turnWaterfall,
} from '~portal/lib/inspector';
import {
  conversationCredits,
  creditsFigure,
  type CreditsSource,
  creditsSourceOf,
} from '~portal/lib/usage';

/**
 * @desc The conversation view's shareable state: which turn the stepper shows (1-based `turn`).
 * `turnId` is the deep-link form for callers that know a turn's id but not its position (the
 * per-user feedback list): resolved against the loaded turns, it applies only while no `turn` is
 * set, and the stepper's first move replaces it with a `turn`.
 */
type ConversationSearch = { turn?: number; turnId?: string };

export const Route = createFileRoute('/_protected/admin/inspector_/$conversationId')({
  validateSearch: (search: Record<string, unknown>): ConversationSearch => {
    const turn = parseTurnParam(search.turn);
    const turnId = typeof search.turnId === 'string' && search.turnId ? search.turnId : undefined;
    return {
      ...(turn === undefined ? {} : { turn }),
      ...(turnId === undefined ? {} : { turnId }),
    };
  },
  component: AdminInspectorConversationPage,
});

/** @desc Id of the stepper, the scroll target when an artefact's turn link is followed. */
const TURNS_ANCHOR = 'turns';

/** @desc The wall-clock session span as `admin.getConversation` serves it, null with no prompt. */
type ConversationSession = Conversation.SessionSpan | null;

const glanceStats = (
  g: ConversationGlance,
  artifacts: Artifact.FormatCounts | undefined,
  updatedAt: Date | string,
  createdAt: Date | string,
  session: ConversationSession,
  credits: { source: CreditsSource; conversationId: string },
): Stat[] => {
  const creditsShown = creditsFigure(credits.source, conversationCredits(credits.conversationId));
  const unfinished = g.turnCount - g.completedTurnCount;
  const sessionLabel = sessionDisplay(session);
  return [
    ...(session
      ? [
          {
            label: 'Asked',
            value: formatDateTime(session.startedAt),
            hint: `${formatUtcDateTime(session.startedAt)} — the time shown is in your local timezone`,
          },
        ]
      : []),
    {
      label: 'Session',
      value: sessionLabel.label,
      hint: sessionLabel.hint,
    },
    {
      label: 'Updated',
      value: formatRelative(updatedAt),
      hint: `Updated ${formatDateTime(updatedAt)} · created ${formatDateTime(createdAt)}`,
    },
    {
      label: 'Turns',
      value: unfinished > 0 ? `${g.turnCount} (${unfinished} unfinished)` : String(g.turnCount),
      ...(unfinished > 0
        ? { hint: 'Unfinished turns recorded no finish: still running, or the run died.' }
        : {}),
    },
    { label: 'Avg turn time', value: formatDurationMs(g.avgTurnElapsedMs) },
    {
      label: 'Total turn time',
      value: g.completedTurnCount > 0 ? formatDurationMs(g.totalTurnElapsedMs) : NO_VALUE,
    },
    { label: 'Model calls', value: formatCount(g.modelCalls) },
    { label: 'Tool calls', value: formatCount(g.toolCalls) },
    {
      label: 'Errors',
      value: formatCount(g.errors),
      hint: `Failed tool calls plus failed model calls, across ${g.turnsWithErrors} turn${g.turnsWithErrors === 1 ? '' : 's'}`,
      alert: g.errors > 0,
    },
    {
      label: 'Tokens',
      value: formatCompact(g.tokens.total),
      hint: `${formatCount(g.tokens.input)} in · ${formatCount(g.tokens.output)} out · ${formatCount(
        g.tokens.cacheRead + g.tokens.cacheWrite5m + g.tokens.cacheWrite1h,
      )} cache`,
    },
    {
      label: 'Credits',
      value: creditsShown.recorded ? (
        creditsShown.text
      ) : (
        <Text size="sm" c="dimmed" fs="italic">
          {creditsShown.text}
        </Text>
      ),
      hint: creditsShown.hint,
    },
    {
      label: 'Artefacts',
      value: artifacts ? <ArtifactFormatIcons counts={artifacts} empty={NO_VALUE} /> : NO_VALUE,
    },
  ];
};

function AdminInspectorConversationPage() {
  const { conversationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const conversationQuery = api.admin.getConversation.useQuery({ conversationId });
  // Same query key as the artefacts panel's, so the two share one request.
  const artifactsQuery = api.admin.listConversationArtifacts.useQuery({ conversationId });
  const creditsQuery = api.usage.credits.useQuery({ conversationIds: [conversationId] });
  const conversation = conversationQuery.data;
  const ownerId = conversation?.refs.userId;
  const emailsQuery = api.admin.getUserEmails.useQuery(
    { userIds: ownerId ? [ownerId] : [] },
    { enabled: Boolean(ownerId) },
  );

  const turns = useMemo(
    () => (conversation ? turnWaterfall(conversation.history) : []),
    [conversation],
  );
  const glance = useMemo(() => conversationGlance(turns), [turns]);
  const current = clampTurn(search.turn ?? turnNumberOf(turns, search.turnId), turns.length);

  const showTurn = (turn: number, scroll = false) => {
    navigate({
      search: (prev) => ({ ...prev, turn, turnId: undefined }),
      replace: true,
      resetScroll: false,
      ...(scroll
        ? { hash: TURNS_ANCHOR, hashScrollIntoView: { behavior: 'smooth', block: 'start' } }
        : {}),
    }).catch((err) => {
      logger.error('[AdminInspector] Navigation failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return (
    <Stack gap="lg" p="lg">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="sm">
          <ButtonLink
            to="/admin/inspector"
            search={ownerId ? { userId: ownerId } : {}}
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
          >
            Conversations
          </ButtonLink>
          <Title order={1}>{conversation?.summary || 'Conversation'}</Title>
        </Group>
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconRefresh size={16} />}
          loading={conversationQuery.isFetching}
          onClick={() => {
            void conversationQuery.refetch();
            void artifactsQuery.refetch();
            void creditsQuery.refetch();
          }}
        >
          Refresh
        </Button>
      </Group>

      {conversationQuery.isError ? (
        <Alert color="red" title="Could not load conversation">
          {conversationQuery.error.message}
        </Alert>
      ) : !conversation ? (
        <Stack gap="md">
          <Skeleton height={90} />
          <Skeleton height={240} />
        </Stack>
      ) : (
        <>
          <Paper withBorder p="md">
            <Stack gap="sm">
              <Group justify="space-between" wrap="wrap" gap="sm">
                <Group gap="xs" wrap="wrap">
                  <Group gap={2} wrap="nowrap">
                    <Text size="sm" fw={600}>
                      {emailsQuery.data?.[conversation.refs.userId] ?? conversation.refs.userId}
                    </Text>
                    <CopyIdButton value={conversation.refs.userId} label="user id" />
                  </Group>
                  <UserOrganisation userId={ownerId} />
                  <Badge size="sm" variant="light">
                    {conversation.mode}
                  </Badge>
                  {conversation.activeTurn && (
                    <Badge size="sm" variant="light" color="yellow">
                      turn in progress
                    </Badge>
                  )}
                </Group>
                <Group gap="xs" wrap="nowrap">
                  <AnchorLink to="/chat/$conversationId" params={{ conversationId }} size="sm">
                    <Group gap={4} wrap="nowrap">
                      Open in chat
                      <IconExternalLink size={12} aria-hidden />
                    </Group>
                  </AnchorLink>
                  <CopyIdButton value={conversation._id} label="conversation id" />
                </Group>
              </Group>
              <StatStrip
                stats={glanceStats(
                  glance,
                  artifactsQuery.data ? countFormats(artifactsQuery.data) : undefined,
                  conversation.updatedAt,
                  conversation.createdAt,
                  conversation.session,
                  { source: creditsSourceOf(creditsQuery), conversationId },
                )}
              />
            </Stack>
          </Paper>
          <ArtifactsPanel
            conversationId={conversationId}
            turnNumberOf={(turnId) => turnNumberOf(turns, turnId)}
            onSelectTurn={(turn) => showTurn(turn, true)}
          />
          <TurnStepper
            id={TURNS_ANCHOR}
            turns={turns}
            current={current}
            onChange={(turn) => showTurn(turn)}
            datadogSite={getAppEnv().datadog.site}
          />
        </>
      )}
    </Stack>
  );
}
